import { useEffect } from "react";
import { useTheme } from "next-themes";

import { useSetCssViewportVars } from "@/shared/hooks/useSetCssViewportVars";
import { usePreventDocumentScroll } from "@/shared/hooks/usePreventDocumentScroll";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";

/**
 * To avoid the initial theme flash, inside index.html, we set the
 * initial theme to light or dark. This handler will remove the initial
 * theme class from the document element after the app is loaded.
 *
 * @returns
 */
export function useUI() {
  const LIGHT_THEME = "initial-light-theme";
  const DARK_THEME = "initial-dark-theme";
  const { setTheme } = useTheme();
  const { appSettings } = useBoundStore(useShallow((state) => ({ appSettings: state.settings.app })));
  const { devicePlatform, deviceType } = useBoundStore(
    useShallow((state) => ({ devicePlatform: state.device.platform, deviceType: state.device.type }))
  );

  useSetCssViewportVars({ disabled: deviceType !== "mobile" });
  usePreventDocumentScroll({ disabled: deviceType !== "mobile" });

  useEffect(() => {
    document.documentElement.classList.remove(LIGHT_THEME);
    document.documentElement.classList.remove(DARK_THEME);
  }, []);

  // Set theme from user settings
  useEffect(() => {
    setTheme(appSettings.theme);
  }, [appSettings.theme, setTheme]);

  // Set platform-specific class names
  useEffect(() => {
    // Remove any existing platform classes first
    document.documentElement.classList.remove("android", "ios", "web");

    // Add the current platform class
    if (devicePlatform === "android") {
      document.documentElement.classList.add("android");
    } else if (devicePlatform === "ios") {
      document.documentElement.classList.add("ios");
    } else if (devicePlatform === "web") {
      document.documentElement.classList.add("web");
    }
  }, [devicePlatform]);

  return null;
}
